import { createRouter, createWebHistory, Router } from "vue-router";
import { getCookie } from "@/utils/storage/cookie";
import { isProduction } from "@/config/constants";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "@/utils/storage/localstorage";
import { MOBILE_BREAKPOINT } from "@/composables/useViewport";

const mainIndex = () => import("@/views/index.vue");
const notFound = () => import("@/views/common/notFound.vue");
const videoEnhancer = () => import("@/views/videoEnhancer/viewWrapper.vue");
const videoEnhancerConfig = () => import("@/views/videoEnhancer/config.vue");
const videoEnhancerEditor = () => import("@/views/videoEnhancer/editor.vue");
const videoEnhancerRecentTasks = () =>
  import("@/views/videoEnhancer/recentTasks.vue");
const videoEnhancerPreviewPage = () =>
  import("@/views/videoEnhancer/previewPage.vue");
const imageEnhancer = () => import("@/views/imageEnhancer/viewWrapper.vue");
const imageEnhancerConfig = () =>
  import("@/views/imageEnhancer/desktop/config.vue");
const imageEnhancerEditor = () =>
  import("@/views/imageEnhancer/desktop/editor.vue");

const routes = [
  {
    path: "/",
    redirect: () => {
      const isMobile =
        typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;
      return isMobile ? "enhance-image-online/config" : "enhance-video-online/config";
    },
    component: mainIndex,
    children: [
      {
        path: "enhance-video-online",
        component: videoEnhancer,
        children: [
          {
            path: "",
            redirect: { name: "videoEnhancerConfig" },
          },
          {
            path: "config",
            name: "videoEnhancerConfig",
            component: videoEnhancerConfig,
            meta: {
              title: "视频高清",
            },
          },
          {
            path: "editor",
            name: "videoEnhancerEditor",
            component: videoEnhancerEditor,
            meta: {
              title: "视频高清",
            },
          },
          {
            path: "previewPage",
            name: "videoEnhancerPreviewPage",
            component: videoEnhancerPreviewPage,
            meta: {
              title: "预览页面",
            },
          },
          {
            path: "recentTasks",
            name: "videoEnhancerRecentTasks",
            component: videoEnhancerRecentTasks,
            meta: {
              title: "最近任务",
            },
          },
        ],
      },
      {
        path: "enhance-image-online",
        component: imageEnhancer,
        children: [
          {
            path: "",
            redirect: { name: "imageEnhancerConfig" },
          },
          {
            path: "config",
            name: "imageEnhancerConfig",
            component: imageEnhancerConfig,
            meta: {
              title: "图片高清",
            },
          },
          {
            path: "editor",
            name: "imageEnhancerEditor",
            component: imageEnhancerEditor,
            meta: {
              title: "图片高清",
            },
          },
        ],
      },
    ],
  },
  {
    name: "404",
    path: "/:pathMatch(.*)*",
    component: notFound,
  },
];

const CHANNEL_QUERY_KEY = "channel";
const BAIDU_AI_MARKET_CHANNEL = "baidu-ai-market";
const BAIDU_AI_MARKET_STORAGE_KEY = "router_channel_baidu_ai_market";

const getRouteChannel = (channel: unknown): string | undefined => {
  if (Array.isArray(channel)) {
    return typeof channel[0] === "string" ? channel[0] : undefined;
  }
  return typeof channel === "string" ? channel : undefined;
};

const getPersistedChannel = (): string | null => {
  const storedChannel = getLocalStorage(BAIDU_AI_MARKET_STORAGE_KEY);
  return storedChannel === BAIDU_AI_MARKET_CHANNEL ? storedChannel : null;
};

const withPersistedChannel = (
  target: { name?: string; path?: string; query?: Record<string, any>; hash?: string; replace?: boolean },
  channel: string | null
) => {
  if (!channel || target.query?.[CHANNEL_QUERY_KEY]) {
    return target;
  }

  return {
    ...target,
    query: {
      ...(target.query || {}),
      [CHANNEL_QUERY_KEY]: channel,
    },
  };
};

const router: Router = createRouter({
  history: createWebHistory("/video/"),
  routes,
});
router.beforeEach(async (to, from, next) => {
  const routeChannel = getRouteChannel(to.query[CHANNEL_QUERY_KEY]);
  const isInitialNavigation = from.matched.length === 0;
  const isVideoEnhancerHome =
    to.name === "videoEnhancerConfig" ||
    to.path.includes("/enhance-video-online/config");

  if (isInitialNavigation && isVideoEnhancerHome) {
    if (routeChannel === BAIDU_AI_MARKET_CHANNEL) {
      setLocalStorage(BAIDU_AI_MARKET_STORAGE_KEY, routeChannel);
    } else {
      removeLocalStorage(BAIDU_AI_MARKET_STORAGE_KEY);
    }
  }

  const persistedChannel = getPersistedChannel();

  if (!routeChannel && persistedChannel) {
    next(
      withPersistedChannel(
        {
          path: to.path,
          query: { ...to.query },
          hash: to.hash,
          replace: true,
        },
        persistedChannel
      )
    );
    return;
  }

  let title = to.meta.title as string;
  document.title = title || "HitPaw Online";

  const isConfigPage =
    to.name === "videoEnhancerConfig" ||
    to.name === "videoEnhancerEditor" ||
    to.name === "imageEnhancerConfig" ||
    to.name === "imageEnhancerEditor" ||
    to.path.includes("/enhance-video-online/config") ||
    to.path.includes("/enhance-video-online/editor") ||
    to.path.includes("/enhance-image-online/config") ||
    to.path.includes("/enhance-image-online/editor");
  const isVideoEnhancerRoute = to.path.includes("/enhance-video-online");
  const isImageEnhancerRoute = to.path.includes("/enhance-image-online");

  // 检查 cookie 中是否有对应的 accessToken，如果没有则自动退出登录（包括 config 页面）
  // 根据环境模式判断使用哪个 cookie：生产环境使用 "production-accessToken"，其他环境使用 "test-accessToken"
  const accessTokenKey = isProduction
    ? "production-accessToken"
    : "test-accessToken";
  // const accessToken = getCookie(accessTokenKey);

  // if (!accessToken) {
  //   console.log(`未找到 ${accessTokenKey} cookie，自动退出登录`);
  //   await logout(true);
  //   // logout 函数内部会调用 backToIndex() 进行路由跳转，所以这里不需要调用 next()
  //   return;
  // }

  // 检查用户登录状态，未登录用户只能访问 config 页面
  // 如果是视频增强相关路由且不是 config 页面，需要检查登录状态
  // 注意：最近任务页允许未登录访问（显示空状态），故排除
  const isRecentTasksPage =
    to.name === "videoEnhancerRecentTasks" ||
    to.path.includes("/enhance-video-online/recentTasks");
  if (isVideoEnhancerRoute && !isConfigPage && !isRecentTasksPage) {
    // 检查 cookie 中的 token 作为备用方案
    const token = getCookie(accessTokenKey);
    if (!token) {
      // 如果没有 token，重定向到 config 页面
      next(
        withPersistedChannel(
          { name: "videoEnhancerConfig" },
          persistedChannel
        )
      );
      return;
    }
  }

  // 图片增强路由检查
  if (isImageEnhancerRoute && !isConfigPage) {
    const token = getCookie(accessTokenKey);
    if (!token) {
      next(
        withPersistedChannel(
          { name: "imageEnhancerConfig" },
          persistedChannel
        )
      );
      return;
    }
  }

  next();
});
router.afterEach(async (to, from) => {
  // 移除 Pinia 依赖，避免初始化问题
  // setIsJump 功能可以在组件内部通过 watch 路由变化来实现
});
export default router;
